import {Payments, Registration} from "./Types.ts";
import {sum, toFixed} from "./Utils.ts";

const YEAR = new Date().getFullYear();

export type PricingLineItem = { quantity: number, price: number}
export type PricingInfo = {
    total: number,
    P0: PricingLineItem,
    P1: PricingLineItem,
    P2: PricingLineItem,
    P3: PricingLineItem,
    P4: PricingLineItem,
    usedDate: number,
    usedDateRationale: string,
    usedDatePeriod: string
}

export function calculateCost(registration: Registration, paymentsForDate: Payments): PricingInfo {
    // date of first payment is used to calculate late pricing vs not.
    const ret: PricingInfo = {
        total: 0,
        P0: {quantity: 0, price: 0},
        P1: {quantity: 0, price: 0},
        P2: {quantity: 0, price: 0},
        P3: {quantity: 0, price: 0},
        P4: {quantity: 0, price: 0},
        usedDate: 0,
        usedDateRationale: '',
        usedDatePeriod: '',
    };
    if (!paymentsForDate?.payments?.length) {
        ret.usedDate = Date.now();
        ret.usedDateRationale = 'No payments, using current date.';
    } else {
        ret.usedDate = Math.min(...(paymentsForDate?.payments ?? []).map(p => p.date));
        ret.usedDateRationale = 'Using date of first payment.';
    }
    let period = 2; // If before March 22, period = 0, if before April 6th, period = 1, if after April 6th, period = 2.
    const March22 = new Date(YEAR, 2, 22);
    const April6 = new Date(YEAR, 3, 6);
    if (ret.usedDate < April6.getTime()) {
        period = 1;
    }
    if (ret.usedDate < March22.getTime()) {
        period = 0;
    }
    ret.usedDatePeriod = ['Before 3/22', '3/22 - 4/6', 'After 4/6'].at(period);
    ret.P0.price = 0; // 3 and under
    ret.P1.price = [0, 75, 125][period]; // children registered for RE
    ret.P2.price = [75, 100, 125][period]; // children not registered for RE

    // <option value="I can pay more than my fair share">I can pay more than my fair share</option>
    // <option value="I can pay my fair share">I can pay my fair share</option>
    // <option value="I can pay a reduced rate">I can pay a reduced rate</option>
    // <option value="Please waive my registration fees">Please waive my registration fees</option>
    // <option value="Specialized Fees">Specialized Fees</option>
    switch (registration.abilityToPay) {
        case 'I can pay more than my fair share':
            ret.P3.price = [175, 175, 225][period];
            break;
        case 'I can pay my fair share':
            ret.P3.price = [175, 175, 225][period];
            break;
        case 'I can pay a reduced rate':
            ret.P3.price = [100, 175, 225][period];
            break;
        case 'Please waive my registration fees':
            ret.P3.price = [0, 0, 0][period];
            break;
        case 'Specialized Fees':
            ret.P3.price = [0, 0, 0][period];
    }
    ret.P4.price = [60, 75, 100][period];

    const day_tripper = ['day-tripper', 'off-site-sat-meals'].includes(registration.sleepingArrangementChoice);

    for (const attendee of (registration.attendees ?? [])) {
        if (attendee.ageOnApril24  == '18 or over') { // Regular adult
            if (day_tripper) ret.P4.quantity++;
            else ret.P3.quantity++;
            continue;
        }
        const age = parseInt(attendee.ageOnApril24);
        if (age <= 3) {
            ret.P0.quantity++;
            continue;
        }
        // only P1 and P2 left at this point.
        if (attendee.registeredForRE) {
            ret.P1.quantity++;
        } else {
            ret.P2.quantity++;
        }
    }
    ret.total = ret.P0.price * ret.P0.quantity + ret.P1.price * ret.P1.quantity + ret.P2.price * ret.P2.quantity + ret.P3.price * ret.P3.quantity + ret.P4.price * ret.P4.quantity;
    const donation = Math.max(registration.donationAmount, 0);
    ret.total += registration.abilityToPay === 'I can pay more than my fair share' ? donation : 0;
    return ret;
}

export function renderPricingInfo(registration: Registration, pricingInfo: PricingInfo, payments: Payments): string {
    const donation = (registration.abilityToPay === 'I can pay more than my fair share' && ((registration.donationAmount ?? 0) !== 0)) ? `
        <tr>
            <td colspan="3">Donation Amount - <b>thank you for your donation! 🙌</b></td>
            <td>$${toFixed(registration.donationAmount ?? 0, 0)}</td>
        </tr>` : '';
    const totalPaid = sum(...(payments?.payments ?? []).map(e => e.amount));
    const paymentsMade = payments?.payments?.map(payment => `
            <tr>
                <td colspan="3">Payment on ${new Date(payment.date).toLocaleString()}</td>
                <td>-$${toFixed(payment.amount, 0)}</td>
            </tr>
        `
    ).join('')
    return `
        <div style="overflow-x: scroll;">
            <table>
                <thead>
                <tr>
                    <th>Category</th>
                    <th>Number of People</th>
                    <th>Fee Each Person (${pricingInfo.usedDatePeriod} pricing)</th>
                    <th>Subtotal</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>Children 3 and under</td>
                    <td>${toFixed(pricingInfo.P0.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P0.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P0.price * pricingInfo.P0.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Youth Registered for RE</td>
                    <td>${toFixed(pricingInfo.P1.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P1.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P1.price * pricingInfo.P1.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Youth Not Registered for RE</td>
                    <td>${toFixed(pricingInfo.P2.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P2.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P2.price * pricingInfo.P2.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Adult</td>
                    <td>${toFixed(pricingInfo.P3.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P3.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P3.price * pricingInfo.P3.quantity, 0)}</td>
                </tr>
                <tr>
                    <td>Adult Saturday Only</td>
                    <td>${toFixed(pricingInfo.P4.quantity, 0)}</td>
                    <td>$${toFixed(pricingInfo.P4.price, 0)}</td>
                    <td>$${toFixed(pricingInfo.P4.price * pricingInfo.P4.quantity, 0)}</td>
                </tr>
                ${donation}
                <tr>
                    <td>Total Registration Cost</td>
                    <td></td>
                    <td></td>
                    <td>$${toFixed(pricingInfo.total, 0)}</td>
                </tr>
                ${paymentsMade}
                <tr>
                    <td>Balance</td>
                    <td></td>
                    <td></td>
                    <td>$${toFixed(pricingInfo.total - totalPaid, 0)}</td>
                </tr>
                </tbody>
            </table>
        </div>`
}